// Safe localStorage wrapper.
//
// Some browsers (Falkon, Firefox in strict privacy mode, Safari private mode,
// certain hardened or WebView setups) either return null for `window.localStorage`
// or throw SecurityError on access. Calling `localStorage.getItem(...)` then
// crashes the React tree during the initial render, which freezes the page.
//
// This module gives the rest of the app a single, non-throwing API so every
// call site stops needing its own try/catch.

const hasWindow = typeof window !== 'undefined';

function getStore() {
  if (!hasWindow) return null;
  try {
    return window.localStorage || null;
  } catch (_err) {
    return null;
  }
}

export function safeGet(key) {
  const store = getStore();
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch (_err) {
    return null;
  }
}

export function safeSet(key, value) {
  const store = getStore();
  if (!store) return false;
  try {
    store.setItem(key, value);
    return true;
  } catch (_err) {
    return false;
  }
}

export function safeRemove(key) {
  const store = getStore();
  if (!store) return false;
  try {
    store.removeItem(key);
    return true;
  } catch (_err) {
    return false;
  }
}

export function safeGetJSON(key, fallback = null) {
  const raw = safeGet(key);
  if (raw === null || raw === undefined) return fallback;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return fallback;
  }
}

export function safeSetJSON(key, value) {
  try {
    return safeSet(key, JSON.stringify(value));
  } catch (_err) {
    return false;
  }
}

export function isStorageAvailable() {
  return getStore() !== null;
}

export default {
  safeGet,
  safeSet,
  safeRemove,
  safeGetJSON,
  safeSetJSON,
  isStorageAvailable,
};
